import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useReportDateRange } from '@/hooks/useReportDateRange';
import { reportsApi } from '@/services/api';
import { downloadCsv } from '@/utils/csv';
import PurchaseReportFilters from '../shared/PurchaseReportFilters';
import ReportDataTable, { columnsToCsvHeaders, rowsToCsvData } from '../shared/ReportDataTable';
import ReportLayout from '../shared/ReportLayout';
import ReportSummaryCards from '../shared/ReportSummaryCards';
import { fetchPurchaseReportExport, usePurchaseReport } from '../shared/usePurchaseReport';
import { paymentColumns, PaymentRow } from './columns';
import { formatMoney } from '@/utils/currency';

export default function PurchasePaymentReportPage() {
  const dateRangeHook = useReportDateRange('This Month');
  const { from_date, to_date } = dateRangeHook;
  const [supplier, setSupplier] = useState('');
  const [supplierLabel, setSupplierLabel] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [user, setUser] = useState('');

  const { data: paymentMethods = [] } = useQuery({
    queryKey: ['reportPaymentMethods'],
    queryFn: () => reportsApi.getReportPaymentMethods(),
  });

  const params = {
    from_date,
    to_date,
    supplier: supplier || undefined,
    payment_method: paymentMethod || undefined,
    user: user || undefined,
  };

  const { rows, total, page, setPage, pageSize, summary, isLoading } = usePurchaseReport({
    queryKey: 'purchasePaymentReport',
    params,
    fetcher: (p) => reportsApi.getPurchasePaymentReport(p),
  });

  const handleExport = async () => {
    const data = await fetchPurchaseReportExport(reportsApi.getPurchasePaymentReport, params);
    downloadCsv(
      `purchase-payments-${from_date}-${to_date}.csv`,
      columnsToCsvHeaders(paymentColumns),
      rowsToCsvData(data.rows as unknown as PaymentRow[], paymentColumns),
    );
  };

  return (
    <ReportLayout
      onExport={handleExport}
      exportDisabled={!total}
      isLoading={isLoading}
      isEmpty={!rows.length}
      page={page}
      total={total}
      pageSize={pageSize}
      onPageChange={setPage}
      filters={
        <PurchaseReportFilters
          dateRangeHook={dateRangeHook}
          supplier={supplier}
          supplierLabel={supplierLabel}
          onSupplierChange={(id, label) => {
            setSupplier(id);
            setSupplierLabel(label);
          }}
          paymentMethod={paymentMethod}
          onPaymentMethodChange={setPaymentMethod}
          user={user}
          onUserChange={setUser}
          paymentMethods={paymentMethods}
          showPaymentMethod
          showUser
        />
      }
      summary={
        <ReportSummaryCards
          items={[
            { label: 'Cash Payments', value: `${formatMoney(summary.cash_payments as number)}` },
            { label: 'Bank Payments', value: `${formatMoney(summary.bank_payments as number)}` },
            { label: 'Cheque Payments', value: `${formatMoney(summary.cheque_payments as number)}` },
            { label: 'Online Payments', value: `${formatMoney(summary.online_payments as number)}` },
            { label: 'Total Paid', value: `${formatMoney(summary.total_paid as number)}`, tone: 'success' },
          ]}
          columns={5}
        />
      }
    >
      <ReportDataTable data={rows as never[]} columns={paymentColumns} />
    </ReportLayout>
  );
}
